import { Component } from '@theme/component';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

/** localStorage key and values, as in the handoff. */
const STORAGE_KEY = 'fls-popup';
/** Set just before the pop-up's form posts, so the reloaded page knows the result is the pop-up's. */
const SUBMITTED_KEY = 'fifi-popup-submitted';
/** The "You're in" line stays up this long before the pop-up hides for good (handoff: 3.6s). */
const DONE_DELAY = 3600;
/** Opens once the page has scrolled this share of a screen. */
const SCROLL_TRIGGER = 0.6;

/**
 * @typedef {object} Refs
 * @property {HTMLElement} card - The pop-up card (non-modal dialog).
 * @property {HTMLButtonElement} ball - The minimised "10% off" ball.
 * @property {HTMLInputElement} [email]
 * @property {HTMLElement} [error] - Server-rendered form error.
 * @property {HTMLElement} [success] - Server-rendered "You're in" line.
 */

/**
 * Fifi's V3 email pop-up (design-handoff/design/SiteChrome.dc.html). Rendered by
 * snippets/fifi-email-popup.liquid when enabled in theme settings.
 *
 * Opens after `data-delay` seconds or once the page has scrolled 60% of a screen (reading the scroll
 * container Horizon uses: .page-wrapper from 990px, the window below). ✕ or Esc minimises it to the
 * ball, which reopens it. The form is Shopify's customer form, which reloads the page; on the way
 * back the pop-up shows its success line (or the form error) and then hides for good.
 * localStorage['fls-popup'] remembers `min` / `done`; the theme editor ignores it.
 *
 * @extends {Component<Refs>}
 */
class FifiEmailPopup extends Component {
  requiredRefs = ['card', 'ball'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  #armed = false;

  connectedCallback() {
    super.connectedCallback();

    const { card } = this.refs;
    card.addEventListener('keydown', this.#onKeyDown);
    card.addEventListener('submit', this.#onSubmit);

    const designMode = Boolean(window.Shopify?.designMode);
    const submitted = !designMode && readSession(SUBMITTED_KEY) === '1';
    if (submitted) removeSession(SUBMITTED_KEY);

    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    if (posted && !designMode) writeState('done');

    if (submitted && this.refs.success) {
      this.#open();
      this.#timer = setTimeout(() => this.#hideForGood(), DONE_DELAY);
      return;
    }

    if (submitted && this.refs.error) {
      this.#open();
      return;
    }

    const state = designMode ? null : readState();
    if (state === 'done') return;
    if (state === 'min') {
      this.refs.ball.hidden = false;
      return;
    }

    this.#arm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disarm();
    clearTimeout(this.#timer);
    this.refs.card?.removeEventListener('keydown', this.#onKeyDown);
    this.refs.card?.removeEventListener('submit', this.#onSubmit);
  }

  /** ✕: minimise to the ball (or hide for good once subscribed). */
  minimise() {
    const { card, ball, success } = this.refs;
    card.hidden = true;

    if (success) {
      this.#hideForGood();
      return;
    }

    ball.hidden = false;
    if (!window.Shopify?.designMode) writeState('min');
  }

  /** The ball: reopen the pop-up and put the cursor in the email field. */
  reopen() {
    this.#open();
    this.refs.email?.focus();
  }

  #open() {
    this.#disarm();
    this.refs.ball.hidden = true;
    this.refs.card.hidden = false;
  }

  #hideForGood() {
    clearTimeout(this.#timer);
    this.refs.card.hidden = true;
    this.refs.ball.hidden = true;
    if (!window.Shopify?.designMode) writeState('done');
  }

  /** Waits for the delay or the scroll trigger, whichever comes first. */
  #arm() {
    this.#armed = true;
    const delay = Number(this.dataset.delay ?? 7) * 1000;
    this.#timer = setTimeout(() => this.#open(), delay);
    this.#listenToScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#listenToScroll);
  }

  #disarm() {
    if (!this.#armed) return;
    this.#armed = false;
    clearTimeout(this.#timer);
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
    scrollContainerMediaQuery.removeEventListener('change', this.#listenToScroll);
  }

  /** (Re)binds to the current scroll container; it changes across 990px. */
  #listenToScroll = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  };

  #onScroll = () => {
    if (getScrollTop() > window.innerHeight * SCROLL_TRIGGER) this.#open();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.minimise();
    this.refs.ball.focus();
  };

  #onSubmit = () => {
    writeSession(SUBMITTED_KEY, '1');
  };
}

/** @returns {string | null} */
function readState() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** @param {'min' | 'done'} value */
function writeState(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage blocked (private mode): the pop-up just shows again next time.
  }
}

/**
 * @param {string} key
 * @returns {string | null}
 */
function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string} value
 */
function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Without sessionStorage the success line just isn't shown after the reload.
  }
}

/** @param {string} key */
function removeSession(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up.
  }
}

if (!customElements.get('fifi-email-popup')) {
  customElements.define('fifi-email-popup', FifiEmailPopup);
}
